import { useState } from 'react';
import { LuArrowUp, LuSunrise, LuSunset } from 'react-icons/lu';

import Widget from '@/components/Widget/Widget';
import { useDashboardBreakpoint } from '@/features/dashboard/hooks/useDashboardBreakpoint';
import { cn } from '@/lib/cn';

import useWeather from './useWeather';
import { compassFromDeg, formatTimeUntil, pickRestOfDayStrip } from './weatherDerive';
import WeatherDetailModal from './WeatherDetailModal';
import WeatherIcon from './weatherIcons';

function fmtHour(ms: number): string {
  return new Date(ms).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
}

export default function WeatherWidget() {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useWeather();

  const status = isLoading ? 'loading' : isError ? 'error' : !data?.now ? 'empty' : 'loaded';
  const nowMs = Date.now();
  const wide = useDashboardBreakpoint() === 'wide';
  const hourStrip = wide && data?.today ? pickRestOfDayStrip(data.today.hourly, nowMs, 8) : (data?.todayRemainingStrip ?? []);

  return (
    <>
      <Widget
        status={status}
        errorMessage={error instanceof Error ? error.message : 'Failed to load weather'}
        emptyMessage="Weather data unavailable"
        loadingMessage="Loading weather…"
        onOpenDetail={() => setOpen(true)}
      >
        {data && data.now && (
          <div className="@container flex h-full flex-col justify-evenly gap-3">
            {/* Current — sized by tile width; narrow tiles move the condition text onto its own line. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-display text-5xl leading-none font-semibold text-(--text-primary) tabular-nums @xl:text-6xl">
                {Math.round(data.now.tempC)}°
              </span>
              <WeatherIcon iconKey={data.now.condition.iconKey} className="text-5xl @md:text-6xl @xl:text-[5rem]" />
              <div className="order-last basis-full text-xs leading-relaxed text-(--text-tertiary) @md:order-0 @md:basis-auto @md:text-sm">
                <span className="@md:block">
                  {data.now.condition.label} · feels {Math.round(data.now.feelsLikeC)}°
                </span>
                <span className="@md:block">
                  <span className="@md:hidden"> · </span>
                  {Math.round(data.now.humidity)}% · {Math.round(data.now.windMps)} m/s
                  {data.now.windDirDeg != null && (
                    <span
                      role="img"
                      aria-label={`from ${compassFromDeg(data.now.windDirDeg)}`}
                      title={`Wind from ${compassFromDeg(data.now.windDirDeg)}`}
                      className="ml-1 inline-flex align-[-0.125em]"
                      style={{ transform: `rotate(${data.now.windDirDeg + 180}deg)` }}
                    >
                      <LuArrowUp aria-hidden="true" />
                    </span>
                  )}
                </span>
              </div>
              <span className="flex-1" />
              {data.nextSunEvent && (
                <div className="flex items-center gap-2 pr-5">
                  <span aria-hidden="true" className="text-xl leading-none text-(--accent-primary)">
                    {data.nextSunEvent.type === 'sunrise' ? <LuSunrise /> : <LuSunset />}
                  </span>
                  <div className="text-right leading-tight whitespace-nowrap">
                    <div className="font-display text-base font-semibold text-(--text-primary) tabular-nums @xl:text-xl">
                      {fmtHour(data.nextSunEvent.ms)}
                    </div>
                    <div className="text-[11px] text-(--text-muted) tabular-nums @xl:text-xs">
                      <span className="hidden @md:inline">{data.nextSunEvent.type} · </span>
                      in {formatTimeUntil(data.nextSunEvent.ms, nowMs)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Rest-of-day strip */}
            {hourStrip.length >= 2 && (
              <ul className={cn('grid gap-1 text-center', wide ? 'grid-cols-8' : 'grid-cols-6')}>
                {hourStrip.map((s) => (
                  <li key={s.ts} className="flex flex-col items-center">
                    <span className="text-xs text-(--text-muted) tabular-nums">
                      {new Date(s.ts).getHours().toString().padStart(2, '0')}
                    </span>
                    <WeatherIcon iconKey={s.condition.iconKey} className="text-[1.625rem] @xl:text-[2.25rem]" />
                    <span className="tablet:text-base text-sm font-semibold text-(--text-primary) tabular-nums">
                      {Math.round(s.tempC)}°
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* 7-day strip (wide only — there is room) */}
            {wide && data.week.length > 0 && (
              <ul className="grid grid-cols-7 gap-1 border-t border-(--border-subtle) pt-3 text-center">
                {data.week.slice(0, 7).map((d) => (
                  <li key={d.dateMs} className="flex flex-col items-center">
                    <span className="text-xs font-medium text-(--text-tertiary)">{d.weekdayShort}</span>
                    <WeatherIcon iconKey={d.condition.iconKey} className="text-[1.625rem] @xl:text-[2.25rem]" />
                    <span className="text-xs whitespace-nowrap tabular-nums @lg:text-sm">
                      <span className="font-semibold text-(--text-primary)">{Math.round(d.tempMaxC)}°</span>
                      <span className="text-(--text-muted)">/{Math.round(d.tempMinC)}°</span>
                    </span>
                    <span
                      className={cn(
                        'text-[11px] tabular-nums',
                        d.precipSumMm >= 0.1 ? 'font-semibold text-(--wx-rain)' : 'text-(--text-muted)',
                      )}
                    >
                      {d.precipSumMm >= 0.1 ? d.precipSumMm.toFixed(1) : '0'} mm
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Widget>

      <WeatherDetailModal
        open={open}
        onClose={() => setOpen(false)}
        data={data}
        lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt) : null}
        onRefresh={() => refetch()}
      />
    </>
  );
}
